/**
 * Per-tier AI usage limits.
 *
 * Values derived 2026-07-19 from OpenAI costs (gpt-5.4-mini ~$0.004/chat
 * message, gpt-4o-mini-transcribe ~$0.0015/use) against tier pricing of
 * $0 / $3.99 / $9.99 AUD per month, budgeting AI spend at ~30% of net
 * revenue (after GST + Google's 15%) at full utilization; free tier is
 * capped at ~US$0.10/mo. Pro limits are live for any tier='pro' user.
 * Plus is not yet purchasable; its limits are shelf-ready for a future
 * launch. The const map shape stays stable so callers don't change.
 *
 * Per-IP limits are derived by multiplying per-user limits by IP_MULTIPLIER.
 * Rationale: a household of 4 power users on one IP should not be capped by a
 * single account's quota, but a bad actor spinning up 50 accounts on one IP
 * should still hit a wall.
 */

export type TierName = 'free' | 'plus' | 'pro';

export interface TierLimits {
  chatMessagesPerDay: number;
  chatMessagesPerMonth: number;
  suggestionsPerDay: number;
  transcriptionsPerDay: number;
  inputTokensPerDay: number;
  outputTokensPerDay: number;
  imageBytesPerRequest: number;
  imageBytesPerDay: number;
  inputCharacterLimit: number;
}

export const LIMITS_BY_TIER: Record<TierName, TierLimits> = {
  free: {
    chatMessagesPerDay: 5,
    chatMessagesPerMonth: 20,
    suggestionsPerDay: 2,
    transcriptionsPerDay: 5,
    inputTokensPerDay: 30_000,
    outputTokensPerDay: 10_000,
    imageBytesPerRequest: 5 * 1024 * 1024,
    imageBytesPerDay: 10 * 1024 * 1024,
    inputCharacterLimit: 1_000,
  },
  plus: {
    chatMessagesPerDay: 15,
    chatMessagesPerMonth: 100,
    suggestionsPerDay: 5,
    transcriptionsPerDay: 15,
    inputTokensPerDay: 100_000,
    outputTokensPerDay: 30_000,
    imageBytesPerRequest: 5 * 1024 * 1024,
    imageBytesPerDay: 25 * 1024 * 1024,
    inputCharacterLimit: 2_000,
  },
  pro: {
    chatMessagesPerDay: 40,
    chatMessagesPerMonth: 300,
    suggestionsPerDay: 10,
    transcriptionsPerDay: 30,
    inputTokensPerDay: 250_000,
    outputTokensPerDay: 75_000,
    imageBytesPerRequest: 10 * 1024 * 1024,
    imageBytesPerDay: 100 * 1024 * 1024,
    inputCharacterLimit: 4_000,
  },
};

export const IP_MULTIPLIER = 4;
export const SIGNUPS_PER_IP_PER_DAY = 3;

export interface IpLimits {
  chatMessagesPerDay: number;
  suggestionsPerDay: number;
  transcriptionsPerDay: number;
  inputTokensPerDay: number;
  outputTokensPerDay: number;
  imageBytesPerDay: number;
}

export function ipLimitsFor(tier: TierName): IpLimits {
  const base = LIMITS_BY_TIER[tier];
  return {
    chatMessagesPerDay: base.chatMessagesPerDay * IP_MULTIPLIER,
    suggestionsPerDay: base.suggestionsPerDay * IP_MULTIPLIER,
    transcriptionsPerDay: base.transcriptionsPerDay * IP_MULTIPLIER,
    inputTokensPerDay: base.inputTokensPerDay * IP_MULTIPLIER,
    outputTokensPerDay: base.outputTokensPerDay * IP_MULTIPLIER,
    imageBytesPerDay: base.imageBytesPerDay * IP_MULTIPLIER,
  };
}
